import { observer } from 'mobx-react-lite'
import { FC, PropsWithChildren, UIEventHandler, useLayoutEffect, useRef } from 'react'
import { styled } from '../../../../lib/stitches'
import { useTreeState } from '../../../../stores/Root'
import { IInstrumentStemStore } from '../../../../stores/song/stem/InstrumentStemStore'
import { Box } from '../../../core/Components'
import Background from './Background'
import Grid from './Grid'
import Keyboard from './Keyboard'
import KeyScaleManager from './KeyScaleManager'
import Notes from './Notes'
import {
  getPianoRollHeight,
  NOTE_COUNT_NONCHROMATIC,
  NOTE_HEIGHT,
  usePlayNotesFromKeyboard
} from './PianoRollUtils'
import Playhead from './Playhead'
import * as VerticalScrollBox from './VerticalScrollBox'

const Container = styled('div', {
  width: '100%',
  height: 'calc(100% - 46px)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch'
})

let lastScrollPos = (NOTE_COUNT_NONCHROMATIC * NOTE_HEIGHT) / 3 // TODO: write a normal function that will calculate the median note in the note collection in the current loop and make sure this median note is at the center of the viewport.

const Content: FC<PropsWithChildren> = observer(({ children }) => {
  const { studio } = useTreeState()
  const { selectedStemIndex, selectedSection, song } = studio
  const stem = song.stems[selectedStemIndex]
  const { isRemixed } = stem
  const loop = stem.getLoopAtSectionOrNull(selectedSection)
  const { scale, key } = loop.melodicLoop.melodicSettings
  usePlayNotesFromKeyboard({
    stem: stem as IInstrumentStemStore
  })
  useLayoutEffect(() => {
    KeyScaleManager.update({ key, scale })
    console.log('[KeyAndScale] update', { key, scale })
  }, [key, scale])

  const ref = useRef<HTMLDivElement>()

  useLayoutEffect(() => {
    KeyScaleManager.subscribe('container', ({ scale }) => {
      ref.current.style.height = getPianoRollHeight(scale) + 'px'
    })
    return () => KeyScaleManager.unsubscribe('container')
  }, [])

  return (
    <Box
      ref={ref}
      css={{ position: 'relative', overflow: 'hidden', pointerEvents: isRemixed ? 'none' : 'auto' }}
    >
      {children}
    </Box>
  )
})

const CanvasPianoRoll: FC = () => {
  const ref = useRef<HTMLDivElement>()
  useLayoutEffect(() => {
    ref.current.scrollBy({ top: lastScrollPos, behavior: 'smooth' })
  }, [])
  const handleScroll: UIEventHandler<HTMLDivElement> = (evt) => {
    const target = evt.target as HTMLDivElement
    lastScrollPos = target.scrollTop
  }
  return (
    <Container>
      <VerticalScrollBox.Root type="always">
        <VerticalScrollBox.Viewport onScroll={handleScroll} ref={ref}>
          <Content>
            <Keyboard />
            <Background />
            <Grid />
            <Notes />
            <Playhead />
          </Content>
        </VerticalScrollBox.Viewport>
        <VerticalScrollBox.Scrollbar orientation="vertical">
          <VerticalScrollBox.Thumb />
        </VerticalScrollBox.Scrollbar>
        <VerticalScrollBox.Corner />
      </VerticalScrollBox.Root>
    </Container>
  )
}

export default CanvasPianoRoll
